/* eslint-disable no-await-in-loop */
/* eslint-disable prefer-const */
/* eslint-disable no-underscore-dangle */
import fsPromises from 'fs/promises';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { GetStaticPaths, GetStaticProps } from 'next/types';
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import { MDXRemote } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import path from 'path';

import { axios } from '@/library/axios';
import { cookiesStorage } from '@/library/storage';
import blogStyles from '@/styles/blogs/blog.module.css';
import { authConstants } from '@/utils/constants';
import {
  CustomGuideImage,
  CustomIframe,
  CustomImage,
  GuideComponents,
  Heading,
  Primary,
} from '@/utils/constants/blog';

type Cover = {
  data: {
    attributes: {
      name: string;
      width: number;
      height: number;
      url: string;
    };
  };
};

type Content = {
  id: number;
  __component: string;
  content?: any;
  cover?: Cover;
  description?: any;
  image?: any;
};

type Meta = {
  id: number;
  name: string;
  content: string;
  httpEquiz: string;
};

type Attributes = {
  slug: string;
  title: string;
  metaTitle: string;
  meta: Meta[];
  content: Content[];
  isPreview: boolean;
};

export type BlogContent = {
  id: string;
  attributes: Attributes;
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
};

export type Users = {
  id: string;
  userId: string;
};

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const resultData = await axios.get(
      `${process.env.NEXT_PUBLIC_AMPLIFY_STRAPI_BASE_URL}/api/blogs`,
      {
        headers: {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_AMPLIFY_STRAPI_API_TOKEN}`,
        },
      }
    );
    let slugs: any = [];

    if (resultData.status === 200) {
      const data = resultData?.data?.data;
      slugs = Array.isArray(data)
        ? [
            ...data.map((x) => {
              return { params: { slug: x.attributes.slug } };
            }),
          ]
        : [];
    }
    return {
      paths: [...slugs],
      fallback: 'blocking',
    };
  } catch (err) {
    console.log({ axiosError: err });
  }
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params?.slug;

  console.log({ url: process.env.NEXT_PUBLIC_AMPLIFY_STRAPI_BASE_URL });

  try {
    const resultData = await axios.get(
      `${process.env.NEXT_PUBLIC_AMPLIFY_STRAPI_BASE_URL}/api/blogs?populate[content][populate]=*&populate[meta][populate]=*&filters[slug][$eq]=${slug}`,
      {
        headers: {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_AMPLIFY_STRAPI_API_TOKEN}`,
        },
      }
    );
    // const priviledgedUsers = await axios.get(
    //   `${process.env.NEXT_PUBLIC_AMPLIFY_STRAPI_BASE_URL}/api/priviledged-user?populate=*`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${process.env.NEXT_PUBLIC_AMPLIFY_STRAPI_API_TOKEN}`,
    //     },
    //   }
    // )
    const usersPath = path.join(process.cwd(), '/priviledgedUsers.json');
    // reading priviledged users from local file storage.
    const users = JSON.parse(
      await fsPromises.readFile(usersPath, { encoding: 'utf-8' })
    );
    if (users === '' || !Array.isArray(users)) {
      throw new Error('Error! No users data in file or data corrupted');
    }
    if (resultData.status === 200) {
      const data: BlogContent = resultData?.data?.data[0];
      console.log({ data });

      let blogContent: BlogContent = JSON.parse(JSON.stringify(data));

      // blogContent.attributes.meta = await serialize(blogContent.attributes.meta);

      // const content: Content[] = blogContent?.attributes?.content;

      // async function getSerialized(val: string): Promise<MDXRemoteSerializeResult> {
      //   return serialize(val);
      // }

      // const serializedContent = [];
      // blogContent.attributes.meta = await serialize(blogContent.attributes.meta);

      for (let i = 0; i < blogContent?.attributes?.content.length; i += 1) {
        if (
          blogContent?.attributes?.content[i].__component ===
          'blog.blog-rich-text'
        ) {
          // serializedContent.push(
          //   getSerialized(blogContent?.attributes?.content[i].content as string)
          // );
          blogContent.attributes.content[i].content = await serialize(
            blogContent?.attributes?.content[i].content as string
          );
        }

        if (
          blogContent?.attributes?.content[i].__component ===
          'blog.guide-component'
        ) {
          blogContent.attributes.content[i].description = await serialize(
            blogContent?.attributes?.content[i].description as string
          );
        }
      }
      // await Promise.all(serializedContent);

      // console.log({ blogContent: blogContent?.attributes?.content });
      // console.log({ serializedContent });

      return { props: { blogContent, users }, revalidate: 10 };
    }
  } catch (err) {
    console.log({ axiosError: err });
  }

  return { props: { blogContent: null } };
};

const components = {
  h1: Heading.h1,
  h2: Heading.h2,
  h3: Heading.h3,
  h4: Heading.h4,
  h5: Heading.h5,
  h6: Heading.h6,
  strong: Primary.strong,
  em: Primary.em,
  ul: Primary.ul,
  li: Primary.li,
  blackqoute: Primary.blockquote,
  ol: Primary.ol,
  img: CustomImage,
  p: Primary.p,
  iframe: CustomIframe,
};

const guideComponents = {
  ...components,
  p: GuideComponents.p,
};

function Page({
  blogContent,
  users,
}: {
  blogContent: BlogContent;
  users: Users[];
}) {
  const router = useRouter();

  if (!blogContent) {
    return null;
  }

  const { attributes } = blogContent;
  const { title, metaTitle, content, meta, isPreview } = attributes;
  const loggedInUserId = cookiesStorage.getCookie(
    authConstants.OM_AUTH_USER_ID
  );
  if (isPreview) {
    if (users.find((ele: any) => ele.userId !== loggedInUserId)) {
      return null;
    }
  }

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        {meta.map((obj: any) => {
          return <meta key={obj.id} name={obj.name} content={obj.content} />;
        })}
      </Head>
      <main
        className={`${blogStyles['font-helvetica']} home withHeader singleFlow blogs pb-0`}
      >
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li
                className="breadcrumb-item"
                onClick={() => router.push('/blogs')}
              >
                <Link href="">Blogs</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {title}
              </li>
            </ol>
          </nav>
        </div>
        <section className="blogs__contents">
          <div className="blogs__container">
            {content.map((obj, index) => {
              if (obj.__component === 'blog.blog-rich-text') {
                return (
                  <div key={index} className="wrapper">
                    <MDXRemote
                      {...(obj.content as MDXRemoteSerializeResult)}
                      components={components}
                    />
                  </div>
                );
              }

              if (obj.__component === 'blog.guide-component') {
                return (
                  <div key={index} className="wrapper">
                    <div className="d-md-flex mb-5">
                      <CustomGuideImage
                        className={`mr-md-3 img-fluid ${blogStyles['guide-img']}`}
                        src={obj.image.data.attributes.url}
                      />
                      <MDXRemote
                        {...(obj.description as MDXRemoteSerializeResult)}
                        components={guideComponents}
                      />
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </section>
      </main>
    </>
  );
}

export default Page;
